import { Component, Inject, Output, EventEmitter, Input } from "@angular/core";
import { Observable, BehaviorSubject, forkJoin } from "rxjs";

import { LocalDate } from "../../../../shared/jsr310-localdates/types/local-date.type";
import { CommandRelay } from "../../../../shared/commands/command-relay.class";
import { JSR310ConversionService } from "../../../../shared/jsr310-localdates/services/jsr310-conversion.service";

import { ButtonActions } from '../../../../app.constants';
import { StepAction } from "../../entities/step-action-entity";
import { DocumentPaButton } from '../../entities/document-pa-button';
import { DocumentPaService } from "../../services/document-pa-service";
import { ParcTLR } from "../../../structure/parc/entities/parc-tlr.entity";
import { DocumentPersonneAvertie, CategoriePa, EtatDocumentPa } from "../../entities/document-pa.entity";
import { UpdateStepDefinitionOrder } from "../../entities/document-pa-update-step-definition-order.entity";
import { StepDefinitionPaEntity } from "../../entities/document-pa-step-definition-data.entity";

import { ParcService } from "../../../structure/parc/services/parc.service";

@Component({
	selector: 'document-pa-definition',
	templateUrl: 'document-pa-definition.html',
	styleUrls: ['document-pa-definition.css'],
})
export class DocumentPaDefinitionComponent {

	borneSupFin!: LocalDate | null;
	borneInfFin!: LocalDate | null;
	borneSupDebut!: LocalDate | null;
	borneInfDebut!: LocalDate | null;
	actualParcIds: number[] = [];
	duree: number = 0;
	antenneId!: number;
	paCategories: typeof CategoriePa = CategoriePa;
	prerequisChecked: boolean[] = [false, false, false, false];
	differentResponsables$ = new BehaviorSubject<boolean>(false);
	
	paEtats: typeof EtatDocumentPa = EtatDocumentPa;

	@Output() actionRequested = new EventEmitter<StepAction>();
	@Output() categoriePaChange = new EventEmitter<StepDefinitionPaEntity>();
	
	actionButtons: DocumentPaButton[] = [
		new DocumentPaButton(ButtonActions.CANCEL, 'Liste des PA', 'btn btn-danger'),
		new DocumentPaButton(ButtonActions.SAVE, 'Sauvegarder maintenant', 'btn btn-warning', true),
		new DocumentPaButton(ButtonActions.NEXT, 'Next', 'btn btn-success', true),
	];

	private currentPa$ = new CommandRelay<DocumentPersonneAvertie | undefined>(
		(currentPa) => {
			if (currentPa != undefined) {
				this.documentPa = new DocumentPersonneAvertie();
				if (currentPa.id && (currentPa.id > -1 || currentPa.duplicatedId)) {
					this.clearDefinitionForm();
					if (currentPa.duplicatedId) {
						this.documentPa.etat = EtatDocumentPa.BROUILLON;
						this.prerequisChecked = [false, false, false, false];
					} else {
						this.documentPa.etat = currentPa.etat;
						this.prerequisChecked = [true, true, true, true];
					}
					this.documentPaService.getStepDefinitionData(currentPa.id).subscribe(
						(definitionStepData) => {
							this.documentPa.duplicatedId = currentPa.duplicatedId;
							this.documentPa.categorie = currentPa.categorie;
							this.documentPa.id = currentPa.duplicatedId ? -1 : currentPa.id;
							this.documentPa.numeroPa = definitionStepData.numeroPa;
							
							if (currentPa.duplicatedId) {
								this.documentPa.commune = '';
								this.actualParcIds = [];
								this.documentPa.parcIds = [];
							} else {
								this.documentPa.commune = definitionStepData.commune;
								this.actualParcIds = definitionStepData.parcIds;
								this.documentPa.parcIds = definitionStepData.parcIds;
							}

							this.documentPa.description = definitionStepData.description;
							this.documentPa.horaires = definitionStepData.horaires;
							this.documentPa.nombreIntervenants = definitionStepData.nombreIntervenants;
							this.documentPa.dateDebut = Array.isArray(definitionStepData.dateDebut)
								? definitionStepData.dateDebut
								: null;
							this.documentPa.dateFin = Array.isArray(definitionStepData.dateFin)
								? definitionStepData.dateFin
								: null;
							this.evaluerDuree();
							this.evaluerFormulaire();
						}
					);
				} else if (currentPa.id === -1) {
					this.documentPa.id = currentPa.id;
					this.clearDefinitionForm();
					this.documentPa.etat = EtatDocumentPa.BROUILLON;
					// au cas ou le formulaire de creation avait été raffraichit, on remet les données précédemment saisies
					if (currentPa.dateDebut) {
						this.documentPa.dateDebut = currentPa.dateDebut;
					}
					if (currentPa.dateFin) {
						this.documentPa.dateFin = currentPa.dateFin;
					}
					if (currentPa.numeroPa) {
						this.documentPa.numeroPa = currentPa.numeroPa;
					}
					if (currentPa.commune) {
						this.documentPa.commune = currentPa.commune;
					}
					if (currentPa.horaires) {
						this.documentPa.horaires = currentPa.horaires;
					}
					if (currentPa.nombreIntervenants) {
						this.documentPa.nombreIntervenants = currentPa.nombreIntervenants;
					}
					if (currentPa.description) {
						this.documentPa.description = currentPa.description;
					}
					if (currentPa.parcIds) {
						this.actualParcIds = currentPa.parcIds;
						this.documentPa.parcIds = currentPa.parcIds;
					}
					this.evaluerDuree();
					this.evaluerFormulaire();
				}
			}
		}
	);

	@Input() set currentPa(obs: Observable<DocumentPersonneAvertie | undefined>) {
		this.currentPa$.register(obs);
	}
	
	documentPa!: DocumentPersonneAvertie;

	constructor(
		@Inject(JSR310ConversionService) private dateConversionService: JSR310ConversionService,
		@Inject(DocumentPaService) private documentPaService: DocumentPaService,
		@Inject(ParcService) private parcService: ParcService,
	) {

		let todayPlus36Months = new Date();
		todayPlus36Months.setMonth(todayPlus36Months.getMonth() + 36);
		const maxDate = dateConversionService.toLocalDate(todayPlus36Months);
		if (maxDate) {
			this.borneSupFin = maxDate;
		}
		if (localStorage['ngStorage-user']) {
		    let userInfos: any = JSON.parse(localStorage['ngStorage-user'])
		    this.antenneId = userInfos['idAntenne'];
		}
		this.evaluerFormulaire();
	}
	
	evaluerFormulaire(): void {
		if (!this.documentPa) {
			this.actionButtons[1].disabled = true;
			this.actionButtons[2].disabled = true;
			return;
		}

		const isFormValid =
			!!this.documentPa.numeroPa &&
			this.documentPa.parcIds?.length > 0 &&
			!!this.documentPa.commune &&
			!!this.documentPa.dateDebut &&
			!!this.documentPa.dateFin &&
			!!this.documentPa.horaires &&
			this.documentPa.nombreIntervenants !== null &&
			this.documentPa.nombreIntervenants !== undefined;

		const isPrerequisValid = this.prerequisChecked.every(checked => checked === true);
		const responsablesDiff = this.differentResponsables$.getValue();

		this.actionButtons[1].disabled = !isFormValid || responsablesDiff;
		this.actionButtons[2].disabled = !isFormValid || !isPrerequisValid || responsablesDiff;
	}

	onPrerequisChange(): void {
		this.evaluerFormulaire();
	}

	evaluerDuree(): void {
		if (this.documentPa) {
			const service = this.dateConversionService;
	
			// Compute min/max for end date based on start date
			if (this.documentPa.dateDebut) {
				const debut = service.fromMandatoryLocalDate(this.documentPa.dateDebut);
	
				const minFin = new Date(debut);
				const maxFin = new Date(debut);
				maxFin.setFullYear(maxFin.getFullYear() + 3);
	
				this.borneInfFin = service.toLocalDate(minFin) ?? null;
				this.borneSupFin = service.toLocalDate(maxFin) ?? null;
	
				// Adjust dateFin if out of bounds
				if (this.documentPa.dateFin) {
					const fin = service.fromMandatoryLocalDate(this.documentPa.dateFin);
					if (fin < minFin) {
						this.documentPa.dateFin = service.toLocalDate(minFin) ?? null;
					} else if (fin > maxFin) {
						this.documentPa.dateFin = service.toLocalDate(maxFin) ?? null;
					}
				}
			}
			// Compute min/max for start date based on end date
			if (this.documentPa.dateFin) {
				const fin = service.fromMandatoryLocalDate(this.documentPa.dateFin);

				const minDebut = new Date(fin);
				minDebut.setFullYear(minDebut.getFullYear() - 3);
				const maxDebut = new Date(fin);

				this.borneInfDebut = service.toLocalDate(minDebut) ?? null;
				this.borneSupDebut = service.toLocalDate(maxDebut) ?? null;

				// Adjust dateDebut if out of bounds
				if (this.documentPa.dateDebut) {
					const debut = service.fromMandatoryLocalDate(this.documentPa.dateDebut);
					if (debut < minDebut) {
						this.documentPa.dateDebut = service.toLocalDate(minDebut) ?? null;
					} else if (debut > maxDebut) {
						this.documentPa.dateDebut = service.toLocalDate(maxDebut) ?? null;
					}
				}
			}

			// Compute duration in months
			if (this.documentPa.dateDebut && this.documentPa.dateFin) {
				const debut = service.fromMandatoryLocalDate(this.documentPa.dateDebut);
				const fin = service.fromMandatoryLocalDate(this.documentPa.dateFin);

				let months = (fin.getFullYear() - debut.getFullYear()) * 12 + (fin.getMonth() - debut.getMonth());
				if (fin.getDate() < debut.getDate()) months--;
				this.duree = Math.max(months, 0);
				this.documentPa.duree = this.duree;
			} else {
				this.duree = 0;
				this.documentPa.duree = null;
			}

			this.evaluerFormulaire();
		}
	}

    performAction(requestedAction: ButtonActions) {
        console.log('requested action: ', requestedAction);

        switch (requestedAction) {
            case ButtonActions.CANCEL:
                this.actionRequested.emit(
                    new StepAction(null, ButtonActions.RETURN_TO_LIST)
                );
                break;

            case ButtonActions.SAVE:
                this.saveStep1(false); 
                break;

            case ButtonActions.NEXT:
                this.saveStep1(true); 
                break;

            default:
            // do nothing
        }
    }

	clearDefinitionForm(): void {
		this.documentPa.numeroPa = '';
		this.documentPa.commune = '';
		this.documentPa.parcIds = [];
		this.documentPa.description = '';
		this.documentPa.dateDebut = null;
		this.documentPa.dateFin = null;
		this.documentPa.duree = null;
		this.documentPa.horaires = '';
		this.documentPa.version = 1;
		this.documentPa.nombreIntervenants = null;
		this.actualParcIds = [];
		this.prerequisChecked = [false, false, false, false];
		
		this.actionButtons[1].disabled = true;
		this.actionButtons[2].disabled = true;
	}
	
	parcIdshasChanged(parcIds: number[]) {
		if (!parcIds.length) {
			this.differentResponsables$.next(false);
			this.evaluerFormulaire();
			return;
		}

		const isInitialState = this.actualParcIds.length === 0 || this.documentPa.duplicatedId;
		const differentParcsHaveBeenChosen =
			this.actualParcIds.length !== parcIds.length ||
			!this.actualParcIds.every((val, idx) => val === parcIds[idx]);

		if (isInitialState || differentParcsHaveBeenChosen) {

			const formData = new StepDefinitionPaEntity();
			formData.dateDebut = this.documentPa.dateDebut;
			formData.dateFin = this.documentPa.dateFin;
			formData.duree = this.documentPa.duree ?? 0;
			formData.horaires = this.documentPa.horaires;
			formData.nombreIntervenants = this.documentPa.nombreIntervenants ?? 0;
			formData.description = this.documentPa.description;
			formData.numeroPa = this.documentPa.numeroPa;
			formData.commune = this.documentPa.commune;
			formData.parcIds = this.documentPa.parcIds;

			this.actualParcIds = parcIds;
			forkJoin(parcIds.map(id => this.parcService.getParcTLRById(id))).subscribe(
				(parcs: ParcTLR[]) => {
					const responsableIds = new Set(parcs.map($ => $.responsableId));
					this.differentResponsables$.next(responsableIds.size > 1);

					const parc = parcs[0];
					formData.categoriePa = parc.energie === 'Eolienne' ? CategoriePa.EOL : CategoriePa.PV;
					this.categoriePaChange.emit(formData);

					this.evaluerFormulaire();
				},
				(err) => {
					console.error('Erreur lors du chargement des parcs', err);
					this.differentResponsables$.next(false);
					this.evaluerFormulaire();
				}
			);
		}
	}
	
	private saveStep1(goNext: boolean): void {
		if (this.documentPa.id === -1) {
			// CREATE
			this.documentPaService.saveDefinitionStep(this.documentPa).subscribe({
				next: saved => {
					this.documentPa.id = saved.id;
					this.documentPa.version = 1;
					this.documentPa.responsableId = saved.responsableId;
					this.documentPa.lastCompletedStep = 1;

					if (goNext) {
						this.actionRequested.emit(
							new StepAction(this.documentPa, ButtonActions.NEXT)
						);
					} else {
						alert('Les données ont été sauvegardées');
						this.actionRequested.emit(
							new StepAction(null, ButtonActions.RETURN_TO_LIST)
						);
					}
				},
				error: err =>
					console.error('Erreur lors de la sauvegarde (step 1)', err)
			});
		} else {
			// UPDATE
			const update = new UpdateStepDefinitionOrder();
			update.id = this.documentPa.id;
			update.dateDebut = this.documentPa.dateDebut
				? this.dateConversionService.fromMandatoryLocalDate(this.documentPa.dateDebut)
				: null;
			update.dateFin = this.documentPa.dateFin
				? this.dateConversionService.fromMandatoryLocalDate(this.documentPa.dateFin)
				: null;
			update.duree = this.documentPa.duree ?? 0;
			update.horaires = this.documentPa.horaires;
			update.nombreIntervenants = this.documentPa.nombreIntervenants ?? 0;
			update.description = this.documentPa.description;
			update.numeroPa = this.documentPa.numeroPa;
			update.commune = this.documentPa.commune;
			update.parcIds = this.documentPa.parcIds;

			this.documentPaService.updateDefinitionStep(update).subscribe({
				next: saved => {
					this.documentPa.responsableId = saved.responsableId;
					this.documentPa.lastCompletedStep =
						(this.documentPa.lastCompletedStep ?? 0) < 1
							? 1
							: this.documentPa.lastCompletedStep;

					if (goNext) {
						this.actionRequested.emit(
							new StepAction(this.documentPa, ButtonActions.NEXT)
						);
					} else {
						alert('Les données ont été sauvegardées');
						this.actionRequested.emit(
							new StepAction(null, ButtonActions.RETURN_TO_LIST)
						);
					}
				},
				error: err =>
					console.error('Erreur lors de la mise à jour (step 1)', err)
			});
		}
	}

}
